import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import {
  KoAdjustment,
  KoAdjustmentRequest,
  KoOverride,
  KoOverridesView,
  KoProduct,
  LenderChannel,
  LenderRiskRulesApi,
  RiskRulesConfig,
} from '../tokens/lender-risk-rules.token';

/** PUT KO override body. */
interface KoOverrideBody {
  koRules: KoOverride[];
}

/**
 * REST-only implementation of {@link LenderRiskRulesApi} (EM-699 / F2 — EM-707).
 *
 * Calls the story-B3 backend (EM-702) `/api/v1/risk-rules/**` surface through the
 * gateway. REST-only per Rules 07/10; rides the Keycloak bearer via `authInterceptor`.
 * Errors are RFC-7807 and surfaced in the component via `extractBackendErrorMessage`
 * (Rule 09) — notably 422 IMMUTABLE_KO and 409 on copy-from-general for marketplace.
 */
@Injectable()
export class LenderRiskRulesRestService implements LenderRiskRulesApi {
  private readonly http = inject(HttpClient);

  getRiskRules(channel: LenderChannel): Observable<RiskRulesConfig> {
    return this.http.get<RiskRulesConfig>(`${API_PATHS.RISK_RULES}/${channel}`);
  }

  saveRiskRules(channel: LenderChannel, config: RiskRulesConfig): Observable<RiskRulesConfig> {
    return this.http.put<RiskRulesConfig>(`${API_PATHS.RISK_RULES}/${channel}`, config);
  }

  getKoOverrides(channel: LenderChannel, product: KoProduct, lang: string): Observable<KoOverridesView> {
    return this.http.get<KoOverridesView>(`${API_PATHS.RISK_RULES}/${channel}/ko/${product}`, {
      params: { lang: lang || 'en' },
    });
  }

  updateKoOverrides(channel: LenderChannel, product: KoProduct, koRules: KoOverride[]): Observable<KoOverridesView> {
    const body: KoOverrideBody = { koRules };
    return this.http.put<KoOverridesView>(`${API_PATHS.RISK_RULES}/${channel}/ko/${product}`, body);
  }

  requestKoAdjustment(channel: LenderChannel, product: KoProduct, request: KoAdjustmentRequest): Observable<KoAdjustment> {
    return this.http.post<KoAdjustment>(`${API_PATHS.RISK_RULES}/${channel}/ko/${product}/adjustment-requests`, request);
  }

  listKoAdjustments(channel: LenderChannel, product: KoProduct): Observable<KoAdjustment[]> {
    return this.http.get<KoAdjustment[]>(`${API_PATHS.RISK_RULES}/${channel}/ko/${product}/adjustment-requests`);
  }

  copyFromGeneral(channel: LenderChannel): Observable<RiskRulesConfig> {
    return this.http.post<RiskRulesConfig>(`${API_PATHS.RISK_RULES}/${channel}/copy-from-general`, {});
  }

  syncLpa(): Observable<void> {
    return this.http.put<void>(API_PATHS.LPA_RISK_RULES_SYNC, {});
  }
}
